import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Chip, Skeleton } from "@mui/material";
import { getPriorityConfig } from "../../../utils";
import { setRightPanelView, setSelectedCommitment, setSidePanelOpen } from "../../../slice/commitmentTrackingSlice";
import { translate } from "../../../../../common/providers";
import { CustomTooltip, CustomIconButton, icon } from "../../../../../common/components";
import styles from "./CommitmentRow.module.css";
import { isNonEmptyArray } from "../../../../../common/utils";

const CommitmentRow = ({
	commitment,
	selectionMode,
	selectedRows,
	onSelectRow,
	onCommitmentSelect,
	onValidateCommitment,
}) => {
	const dispatch = useDispatch();
	const { selectedCommitment } = useSelector((state) => state.commitmentTracking);
	const selectedTheme = useSelector(({ commitmentTracking }) => commitmentTracking.selectedTheme);
	const launchProgress = useSelector(({ commitmentTracking }) => commitmentTracking.progress);
	const priority = isNonEmptyArray(commitment.priority) ? commitment.priority[0] : commitment.priority;
	const priorityConfig = getPriorityConfig(priority);
	const detectedContent = translate(
		(commitment?.sourceCount === 1 && "commitment-tracking.list.detected-content") ||
			(commitment?.sourceCount > 1 && "commitment-tracking.list.detected-content-plural") ||
			"commitment-tracking.list.no-detected-content",
		{ count: commitment.sourceCount }
	);
	const isInSelection = useMemo(
		() =>
			selectionMode === "exclude"
				? !selectedRows.includes(commitment.commitmentId)
				: selectedRows.includes(commitment.commitmentId),
		[selectionMode, selectedRows, commitment]
	);
	const isSelected = useMemo(
		() => selectedCommitment?.commitmentId === commitment?.commitmentId,
		[selectedCommitment, commitment]
	);
	const handleKeyDown = (e) => {
		if (!launchProgress && (e.key === "Enter" || e.key === " ")) {
			e.preventDefault();
			onCommitmentSelect(commitment);
		}
	};
	const handleClickCommitment = () => {
		if (!launchProgress) {
			onCommitmentSelect({ ...commitment, theme: commitment?.theme || selectedTheme });
		}
	};
	const handleCheckCommitment = (e) => {
		e.stopPropagation();
		onSelectRow(commitment.commitmentId);
	};
	const handleOpenComments = (e) => {
		e.stopPropagation();
		dispatch(setSelectedCommitment(commitment));
		dispatch(setRightPanelView("comments"));
		dispatch(setSidePanelOpen(true));
	};
	const handleValidateCommitment = (e) => {
		const { commitmentId, isValidated } = commitment;
		onValidateCommitment(commitmentId, isValidated, e);
	};
	return (
		<div
			className={styles.listItem}
			data-is-focused={isSelected}
			role="button"
			tabIndex={0}
			onClick={handleClickCommitment}
			onKeyDown={handleKeyDown}
		>
			{(launchProgress && <Skeleton height="var(--spacing-l)" variant="rectangular" width="100%" />) || (
				<>
					<Checkbox
						checked={isInSelection}
						className={styles.checkbox}
						data-has-name={!!commitment.name}
						onClick={handleCheckCommitment}
					/>
					<div className={styles.itemContent}>
						{!!commitment?.name && <div className={styles.commitmentName}>{commitment.name}</div>}
						<div className={styles.itemTitle} data-has-name={!!commitment?.name}>
							{commitment.details}
						</div>
						<div className={styles.itemMeta}>{detectedContent}</div>
					</div>
					<div className={styles.itemActions} data-has-name={!!commitment?.name}>
						<CustomTooltip
							placement="bottom"
							title={translate("commitment-tracking.list.priority-tooltip")}
						>
							<Chip
								className={styles.priorityChip}
								color={priorityConfig.color}
								label={priorityConfig.label}
								size="small"
								style={priorityConfig.style}
								sx={{ "& .MuiChip-label": { padding: "6px 8px" } }}
							/>
						</CustomTooltip>
						<CustomIconButton
							btnClassName={
								styles[
									(!commitment?.hasComments && "commitmentComment__button") ||
										"commitmentComment__button--active"
								]
							}
							color=""
							icon={icon.faComments}
							iconColor={(commitment?.hasComments && "var(--grey-700)") || "var(--grey-300)"}
							size="sm"
							tooltip={translate(
								(commitment?.hasComments && "commitment-tracking.list.comments-tooltip") ||
									"commitment-tracking.list.comments-tooltip.none"
							)}
							tooltipPlacement="bottom"
							variant="outlined"
							onClick={handleOpenComments}
						/>
						<CustomIconButton
							btnClassName={commitment.isValidated ? styles.checkButtonValidated : styles.checkButton}
							color="secondary"
							icon={icon.faCheck}
							iconColor={commitment.isValidated ? "var(--color-white)" : "var(--grey-700)"}
							size="sm"
							tooltip={translate(
								`commitment-tracking.list.validation-tooltip.${
									(!commitment.isValidated && "validated") || "not-validated"
								}`
							)}
							tooltipPlacement="bottom"
							variant="outlined"
							onClick={handleValidateCommitment}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default React.memo(CommitmentRow);
